import type { LoadedProcess } from "../../../workflows/data/processCatalogue";
import type { OutputPartReference } from "../../../../api/content/processDefinitions";
import { expandableNodeIds, isSourceNode, workflowGraph, type GraphNode, type SourceGraphNode, type StepGraphNode } from "../../../workflows/model/workflowGraph";

/**
 * An item a step reads from the project, as the Workflow graph resolves it (F103): the
 * guideline its annotate step reads, a label list, a corpus description.
 */
export interface StepRead {
  /** The step's own name for the input it fills. */
  readonly slot: string;
  readonly name: string;
  /** `VersionRef` of the version shown: the latest one in the project. */
  readonly reference: string;
  /** The role the project's bindings fill it through (PG-5); `null` for an item the step names itself. */
  readonly role: string | null;
}

/**
 * A step of the Thread's Workflow by its progress key (`prepare_subject.expand` inside a
 * sub-workflow), as the Workflow graph identifies it: its own key in the Workflow version
 * that pins it (`stepKey`, `inWorkflow`), how it starts, the pinned step and its bindings.
 */
export type StepInWorkflow = StepGraphNode & {
  readonly progressKey: string;
  /** The name of the Workflow version that pins it (`inWorkflow`); `null` while it can't be read. */
  readonly inWorkflowName: string | null;
  /**
   * Each input slot's producer as the graph resolves it, through sub-workflow exports too:
   * the step by progress key (`fromStep`) and its own output part.
   */
  readonly producers: Readonly<Record<string, OutputPartReference>>;
  /** What it reads from the project, in the order of its inputs. */
  readonly reads: readonly StepRead[];
};

const ROLE_KEY = "role:";

/** The role of a `targetKey` (`role:<name>`), or `null` for an item or version. */
function roleOfKey(key: string): string | null {
  return key.startsWith(ROLE_KEY) ? key.slice(ROLE_KEY.length) : null;
}

/** The one item a source shows, and the role it was found through; `null` for terms or a selection over many items. */
function readItemOf(source: SourceGraphNode): Omit<StepRead, "slot"> | null {
  switch (source.kind) {
    case "artifact":
      return { name: source.artifact.name, reference: source.artifact.reference, role: roleOfKey(source.item) };
    case "item":
      return { name: source.item.name, reference: source.item.reference, role: roleOfKey(source.id.slice("item:".length)) };
    case "selection": {
      if (source.item === null) return null;
      const target = source.selector.kind === "label_list" ? source.selector.label_list : null;
      return { name: source.item.name, reference: source.item.reference, role: target !== null && "role" in target ? target.role : null };
    }
    case "terms":
      return null;
  }
}

/** Every step, sub-workflows opened, keyed by progress key; the graph is the one owner of these identities. */
export function stepsInWorkflow(process: LoadedProcess): ReadonlyMap<string, StepInWorkflow> {
  const graph = workflowGraph(process.workflow, process.catalogue, expandableNodeIds(process.workflow, process.catalogue));
  const nodes = new Map<string, GraphNode>(graph.nodes.map((node) => [node.id, node]));
  const producers = new Map<string, Record<string, OutputPartReference>>();
  const reads = new Map<string, StepRead[]>();
  for (const edge of graph.edges) {
    const source = nodes.get(edge.source);
    const target = nodes.get(edge.target);
    const slot = target?.ports.find((port) => port.id === edge.targetPort)?.name;
    const part = source?.ports.find((port) => port.id === edge.sourcePort)?.name;
    // A step's result reaches a later step as a result edge, or from the input band as a read.
    if (edge.kind !== "then" && source?.kind === "step" && source.progressKey !== null && slot !== undefined && part !== undefined) {
      producers.set(edge.target, { ...producers.get(edge.target), [slot]: { fromStep: source.progressKey, part } });
    }
    const item = source === undefined || !isSourceNode(source) ? null : readItemOf(source);
    if (edge.kind === "read" && item !== null && slot !== undefined) {
      reads.set(edge.target, [...(reads.get(edge.target) ?? []), { slot, ...item }]);
    }
  }
  return new Map(
    graph.nodes.flatMap((node): [string, StepInWorkflow][] =>
      node.kind === "step" && node.progressKey !== null
        ? [[node.progressKey, { ...node, progressKey: node.progressKey, inWorkflowName: process.catalogue.workflows.get(node.inWorkflow)?.name ?? null, producers: producers.get(node.id) ?? {}, reads: reads.get(node.id) ?? [] }]]
        : [],
    ),
  );
}
